import { format, parseISO, addMinutes, isValid, startOfDay, addDays } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import type { TimezoneAwareSlot } from '@/lib/availability/types';

/**
 * Core timezone service for bulletproof timezone handling
 * Follows best practices: UTC storage, explicit conversions, DST safety
 */

// Cache for timezone formatting to avoid repeated calculations
const timezoneInfoCache = new Map<string, TimezoneInfo>();
const timezoneFormatCache = new Map<string, string>();

export interface TimezoneInfo {
  name: string;
  abbreviation: string;
  offset: string;
  offsetMinutes: number;
  isDST: boolean;
  currentTime: string;
}

export interface TimezoneAwareDateTime {
  utc: Date;
  local: Date;
  timezone: string;
  formatted: string;
  isValid: boolean;
}

export interface AvailabilityWindow {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  startTime: string; // "09:00"
  endTime: string;   // "17:00"
  timezone: string;
}

export interface AppointmentData {
  id: string;
  startTimeUtc: string; // Always UTC ISO string
  duration: number; // minutes
  organizerTimezone: string;
  attendeeTimezone?: string;
  createdTimezone: string;
}

/**
 * Auto-detect user's timezone
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC'; // Fallback to UTC
  }
}

/**
 * Validate if timezone is valid using IANA database
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get comprehensive timezone information with caching
 */
export function getTimezoneInfo(timezone: string): TimezoneInfo {
  // Check cache first
  const cacheKey = `${timezone}-${Math.floor(Date.now() / (1000 * 60 * 15))}`; // 15-minute cache
  if (timezoneInfoCache.has(cacheKey)) {
    return timezoneInfoCache.get(cacheKey)!;
  }

  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(now);
    const abbreviation = parts.find(part => part.type === 'timeZoneName')?.value || '';
    
    // Get offset information
    const offset = formatInTimeZone(now, timezone, 'xxx');
    const offsetMinutes = getTimezoneOffsetMinutes(timezone, now);
    
    // Check if currently in DST
    const isDST = isCurrentlyDST(timezone, now);
    
    // Format current time in timezone
    const currentTime = formatInTimeZone(now, timezone, 'h:mm a');
    
    const info: TimezoneInfo = {
      name: timezone,
      abbreviation,
      offset,
      offsetMinutes,
      isDST,
      currentTime
    };
    
    // Cache the result
    timezoneInfoCache.set(cacheKey, info);
    
    return info;
  } catch (error) {
    console.error(`[TimezoneService] Error getting timezone info for ${timezone}:`, error);
    // Return fallback info
    return {
      name: timezone,
      abbreviation: 'UTC',
      offset: '+00:00',
      offsetMinutes: 0,
      isDST: false,
      currentTime: format(new Date(), 'h:mm a')
    };
  }
}

/**
 * Convert local time string to UTC for storage
 */
export function localToUtc(localTimeString: string, timezone: string): Date {
  try {
    // Parse the local time string
    const localDate = parseISO(localTimeString);
    
    if (!isValid(localDate)) {
      throw new Error(`Invalid date string: ${localTimeString}`);
    }
    
    // Convert to UTC using date-fns-tz
    return fromZonedTime(localDate, timezone);
  } catch (error) {
    console.error(`[TimezoneService] Error converting local to UTC:`, error);
    throw new Error(`Failed to convert local time to UTC: ${error.message}`);
  }
}

/**
 * Convert UTC to local time for display
 */
export function utcToLocal(utcDate: Date | string, timezone: string): Date {
  try {
    const date = typeof utcDate === 'string' ? parseISO(utcDate) : utcDate;
    
    if (!isValid(date)) {
      throw new Error(`Invalid UTC date: ${utcDate}`);
    }
    
    // Convert to local timezone using date-fns-tz
    return toZonedTime(date, timezone);
  } catch (error) {
    console.error(`[TimezoneService] Error converting UTC to local:`, error);
    throw new Error(`Failed to convert UTC to local time: ${error.message}`);
  }
}

/**
 * Create timezone-aware datetime object
 */
export function createTimezoneAwareDateTime(
  localTimeString: string, 
  timezone: string
): TimezoneAwareDateTime {
  try {
    const utc = localToUtc(localTimeString, timezone);
    const local = utcToLocal(utc, timezone);
    const formatted = formatTimeWithTimezone(utc, timezone);
    
    return {
      utc,
      local,
      timezone,
      formatted,
      isValid: true
    };
  } catch (error) {
    console.error(`[TimezoneService] Error creating timezone-aware datetime:`, error);
    return {
      utc: new Date(),
      local: new Date(),
      timezone,
      formatted: 'Invalid time',
      isValid: false
    };
  }
}

/**
 * Format time with timezone context for display
 */
export function formatTimeWithTimezone(
  utcDate: Date | string, 
  timezone: string, 
  includeDate: boolean = true
): string {
  try {
    const date = typeof utcDate === 'string' ? parseISO(utcDate) : utcDate;
    
    if (!isValid(date)) {
      return 'Invalid time';
    }
    
    // Use cache for repeated formatting
    const cacheKey = `${date.toISOString()}-${timezone}-${includeDate}`;
    if (timezoneFormatCache.has(cacheKey)) {
      return timezoneFormatCache.get(cacheKey)!;
    }
    
    const timeFormat = includeDate ? 'MMM d, yyyy h:mm a' : 'h:mm a';
    const formattedTime = formatInTimeZone(date, timezone, timeFormat);
    const timezoneInfo = getTimezoneInfo(timezone);
    
    const result = `${formattedTime} ${timezoneInfo.abbreviation}`;
    
    // Cache the result
    timezoneFormatCache.set(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error(`[TimezoneService] Error formatting time with timezone:`, error);
    return 'Invalid time';
  }
}

/**
 * Format time in multiple timezones
 */
export function formatTimeMultiTimezone(
  utcDate: Date | string, 
  timezones: string[]
): Array<{ timezone: string; time: string; info: TimezoneInfo }> {
  return timezones.map(tz => ({
    timezone: tz,
    time: formatTimeWithTimezone(utcDate, tz),
    info: getTimezoneInfo(tz)
  }));
}

/**
 * Check if a date/time is during a DST transition
 */
export function isDSTTransition(date: Date, timezone: string): boolean {
  try {
    // Check one hour before and after
    const before = new Date(date.getTime() - 60 * 60 * 1000);
    const after = new Date(date.getTime() + 60 * 60 * 1000);
    
    const offsetBefore = getTimezoneOffsetMinutes(timezone, before);
    const offsetAfter = getTimezoneOffsetMinutes(timezone, after);
    
    // If offsets differ, we're in a DST transition
    return offsetBefore !== offsetAfter;
  } catch {
    return false;
  }
}

/**
 * Check if timezone is currently in DST
 */
function isCurrentlyDST(timezone: string, date: Date = new Date()): boolean {
  try {
    // Compare current offset with standard time offset (January)
    const currentOffset = getTimezoneOffsetMinutes(timezone, date);
    const januaryOffset = getTimezoneOffsetMinutes(timezone, new Date(date.getFullYear(), 0, 15));
    
    return currentOffset !== januaryOffset;
  } catch {
    return false;
  }
}

/**
 * Get timezone offset in minutes
 */
function getTimezoneOffsetMinutes(timezone: string, date: Date): number {
  try {
    const utcDate = new Date(date.getTime());
    const localDate = toZonedTime(utcDate, timezone);
    
    // Calculate offset in minutes
    const offsetMs = localDate.getTime() - utcDate.getTime();
    return Math.round(offsetMs / (1000 * 60));
  } catch {
    return 0;
  }
}

/**
 * Generate safe time slots avoiding DST transitions
 */
export function generateSafeTimeSlots(
  date: Date, 
  timezone: string, 
  startHour: number = 9, 
  endHour: number = 17, 
  intervalMinutes: number = 30
): Date[] {
  const slots: Date[] = [];
  
  try {
    // Start with the beginning of the day in the target timezone
    const dayStart = startOfDay(utcToLocal(date, timezone));
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const slotTime = new Date(dayStart);
        slotTime.setHours(hour, minute, 0, 0);
        
        // Convert back to UTC
        const utcSlotTime = fromZonedTime(slotTime, timezone);
        
        // Skip if this time doesn't exist due to DST transition
        if (!isDSTTransition(utcSlotTime, timezone)) {
          slots.push(utcSlotTime);
        }
      }
    }
  } catch (error) {
    console.error(`[TimezoneService] Error generating safe time slots:`, error);
  }
  
  return slots;
}

/**
 * Validate scheduling request with timezone awareness
 */
export function validateSchedulingRequest(request: {
  dateTime: string;
  timezone: string;
  duration?: number;
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Validate timezone
  if (!isValidTimezone(request.timezone)) {
    errors.push('Invalid timezone specified');
  }
  
  // Validate time format
  let requestedTime: Date;
  try {
    requestedTime = localToUtc(request.dateTime, request.timezone);
  } catch {
    errors.push('Invalid date/time format');
    return { isValid: false, errors };
  }
  
  // Check if time is in the future
  if (requestedTime <= new Date()) {
    errors.push('Cannot schedule appointments in the past');
  }
  
  // Check for DST transition
  if (isDSTTransition(requestedTime, request.timezone)) {
    errors.push('Selected time falls during a daylight saving transition and is not available');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Create appointment with proper timezone context
 */
export function createAppointment(
  localDateTime: string,
  organizerTimezone: string,
  duration: number = 30,
  attendeeTimezone?: string
): AppointmentData {
  const utcDateTime = localToUtc(localDateTime, organizerTimezone);
  const currentTimezone = getUserTimezone();
  
  return {
    id: crypto.randomUUID(),
    startTimeUtc: utcDateTime.toISOString(),
    duration,
    organizerTimezone,
    attendeeTimezone,
    createdTimezone: currentTimezone
  };
}

/**
 * Generate confirmation message with timezone context
 */
export function generateConfirmationMessage(
  appointment: AppointmentData,
  userTimezone: string
): string {
  const startTime = parseISO(appointment.startTimeUtc);
  const organizerTime = formatTimeWithTimezone(startTime, appointment.organizerTimezone);
  const userTime = formatTimeWithTimezone(startTime, userTimezone);
  
  if (userTimezone === appointment.organizerTimezone) {
    return `Confirmed for ${organizerTime}`;
  } else {
    return `Confirmed for ${userTime} (${organizerTime} organizer time)`;
  }
}

/**
 * Convert a timezone-aware slot to display in a target timezone
 */
export function convertSlotToTimezone(
  slot: { startTime: string; endTime: string; sourceTimezone: string },
  targetTimezone: string,
  targetDate?: Date
): { startTime: string; endTime: string; displayStartTime: string; displayEndTime: string } {
  try {
    // Parse the time strings and create dates in the source timezone
    const [startHour, startMinute] = slot.startTime.split(':').map(Number);
    const [endHour, endMinute] = slot.endTime.split(':').map(Number);
    
    // Use the target date if provided, otherwise use today
    const referenceDate = targetDate || new Date();
    const startDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate(), startHour, startMinute);
    const endDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate(), endHour, endMinute);
    
    // Convert from source timezone to UTC
    const utcStart = fromZonedTime(startDate, slot.sourceTimezone);
    const utcEnd = fromZonedTime(endDate, slot.sourceTimezone);
    
    // Convert from UTC to target timezone
    const targetStart = toZonedTime(utcStart, targetTimezone);
    const targetEnd = toZonedTime(utcEnd, targetTimezone);
    
    // Format the converted times
    const convertedStartTime = format(targetStart, 'HH:mm');
    const convertedEndTime = format(targetEnd, 'HH:mm');
    const displayStartTime = format(targetStart, 'h:mm a');
    const displayEndTime = format(targetEnd, 'h:mm a');
    
    return {
      startTime: convertedStartTime,
      endTime: convertedEndTime,
      displayStartTime,
      displayEndTime
    };
  } catch (error) {
    console.error(`[TimezoneService] Error converting slot from ${slot.sourceTimezone} to ${targetTimezone}:`, error);
    // Fallback to original times
    const [startHour, startMinute] = slot.startTime.split(':').map(Number);
    const [endHour, endMinute] = slot.endTime.split(':').map(Number);
    const referenceDate = targetDate || new Date();
    const startDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate(), startHour, startMinute);
    const endDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate(), endHour, endMinute);
    
    return {
      startTime: slot.startTime,
      endTime: slot.endTime,
      displayStartTime: format(startDate, 'h:mm a'),
      displayEndTime: format(endDate, 'h:mm a')
    };
  }
}

/**
 * Create timezone-aware slot data
 */
export function createTimezoneAwareSlot(
  startTime: string,
  duration: number,
  sourceTimezone: string,
  isAvailable: boolean = true,
  additionalData: Partial<TimezoneAwareSlot> = {}
): TimezoneAwareSlot {
  try {
    const [hours, minutes] = startTime.split(':').map(Number);
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
    const endDate = addMinutes(startDate, duration);
    
    // Convert to UTC for storage
    const utcStartTime = fromZonedTime(startDate, sourceTimezone);
    const utcEndTime = fromZonedTime(endDate, sourceTimezone);
    
    return {
      startTime,
      endTime: format(endDate, 'HH:mm'),
      sourceTimezone,
      utcStartTime,
      utcEndTime,
      isAvailable,
      ...additionalData
    };
  } catch (error) {
    console.error(`[TimezoneService] Error creating timezone-aware slot:`, error);
    throw new Error(`Failed to create timezone-aware slot: ${error.message}`);
  }
}
